import { img, inr } from "@/lib/api";
import { REEL_CATS } from "@/pages/Reels";

const Thumb = (u) => (u ? <img src={img(u)} alt="" className="w-12 h-14 object-cover bg-black" /> : "—");
const Active = (r) => <span className={`font-mono text-[10px] px-2 py-1 ${r.active ? "bg-neon text-black" : "bg-white/10"}`}>{r.active ? "LIVE" : "OFF"}</span>;
const TOP = ["S", "M", "L", "XL", "XXL"], BOT = ["28", "30", "32", "34", "36"];

export const productSchema = (cats) => [
  { name: "title", label: "Title", required: true, wide: true },
  { name: "category", label: "Category", type: "select", options: cats.map((c) => ({ value: c.slug, label: c.name })), required: true },
  { name: "slug", label: "URL slug (auto)" },
  { name: "price", label: "Price (₹)", type: "number", required: true },
  { name: "sale_price", label: "Discounted price (₹)", type: "number" },
  { name: "stock", label: "Stock quantity", type: "number", default: 0 },
  { name: "colors", label: "Colours", type: "list" },
  { name: "sizes", label: "Sizes", type: "multi", options: [...TOP, ...BOT] },
  { name: "tags", label: "Collections", type: "multi", options: ["trending", "new", "bestseller", "limited"] },
  { name: "description", label: "Description", type: "textarea" },
  { name: "images", label: "Images (first = main; add front & back views)", type: "images" },
  { name: "active", label: "Visible on store", type: "bool" },
];
export const productCols = [
  { label: "", render: (r) => Thumb(r.images?.[0]) },
  { label: "Title", render: (r) => <span className="font-semibold">{r.title}</span> },
  { label: "Category", render: (r) => <span className="text-grey text-xs">{r.category}</span> },
  { label: "Price", render: (r) => <span className="font-mono">{inr(r.effective_price)}{r.sale_price && <s className="text-grey ml-1 text-xs">{inr(r.price)}</s>}</span> },
  { label: "Stock", render: (r) => <span className={`font-mono ${r.stock < 10 ? "text-red-400" : ""}`}>{r.stock}</span> },
  { label: "Status", render: Active },
];

export const reelSchema = [
  { name: "title", label: "Title", required: true, wide: true },
  { name: "category", label: "Category", type: "select", options: REEL_CATS, default: REEL_CATS[0] },
  { name: "source", label: "Source", type: "select", options: [{ value: "upload", label: "Uploaded MP4" }, { value: "mp4", label: "Hosted MP4 URL" }, { value: "instagram", label: "Instagram Reel URL" }], default: "upload" },
  { name: "url", label: "Video (upload MP4, paste MP4 URL, or Instagram reel URL)", type: "video", required: true },
  { name: "thumbnail", label: "Thumbnail", type: "image" },
  { name: "order", label: "Display order", type: "number", default: 0 },
  { name: "active", label: "Published", type: "bool" },
];
export const reelCols = [
  { label: "", render: (r) => Thumb(r.thumbnail) },
  { label: "Title", render: (r) => r.title },
  { label: "Category", render: (r) => <span className="text-xs text-grey">{r.category}</span> },
  { label: "Source", render: (r) => <span className="font-mono text-xs uppercase">{r.source}</span> },
  { label: "Status", render: Active },
];

export const animeSchema = [
  { name: "title", label: "Design title", required: true },
  { name: "code", label: "Design code (e.g. 05)" },
  { name: "price", label: "Price from (₹)", type: "number", required: true },
  { name: "product_slug", label: "Linked product slug (for Choose This Design)" },
  { name: "palette", label: "Palette hex colours", type: "list", wide: true },
  { name: "description", label: "Description", type: "textarea" },
  { name: "art", label: "Artwork", type: "image" },
  { name: "front", label: "Front T-shirt mockup", type: "image" },
  { name: "back", label: "Back T-shirt mockup", type: "image" },
  { name: "hoodie", label: "Hoodie mockup", type: "image" },
  { name: "active", label: "Visible", type: "bool" },
];
export const animeCols = [
  { label: "", render: (r) => Thumb(r.art) },
  { label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
  { label: "Title", render: (r) => <span className="font-semibold">{r.title}</span> },
  { label: "Price", render: (r) => <span className="font-mono">{inr(r.price)}</span> },
  { label: "Status", render: Active },
];

export const offerSchema = [
  { name: "title", label: "Offer title", required: true, wide: true },
  { name: "kind", label: "Type", type: "select", options: [{ value: "coupon", label: "Coupon code" }, { value: "auto_qty", label: "Auto: buy N items" }, { value: "banner", label: "Promo banner only" }], default: "coupon" },
  { name: "code", label: "Coupon code" },
  { name: "percent", label: "Discount %", type: "number", default: 0 },
  { name: "min_qty", label: "Min qty (auto offers)", type: "number", default: 0 },
  { name: "category_group", label: "Applies to", type: "select", options: [{ value: "", label: "All products" }, { value: "tshirts", label: "T-shirts only" }] },
  { name: "first_order_only", label: "First order only", type: "bool", hint: "Only on customer's first order", default: false },
  { name: "description", label: "Description", type: "textarea" },
  { name: "active", label: "Active", type: "bool" },
];
export const offerCols = [
  { label: "Title", render: (r) => <span className="font-semibold">{r.title}</span> },
  { label: "Type", render: (r) => <span className="font-mono text-xs uppercase">{r.kind}</span> },
  { label: "Code", render: (r) => <span className="font-mono text-neon">{r.code || "—"}</span> },
  { label: "%", render: (r) => r.percent },
  { label: "Status", render: Active },
];

export const categorySchema = [
  { name: "name", label: "Name", required: true },
  { name: "slug", label: "Slug (auto)" },
  { name: "order", label: "Order", type: "number", default: 0 },
  { name: "image", label: "Image", type: "image" },
];
export const categoryCols = [
  { label: "", render: (r) => Thumb(r.image) },
  { label: "Name", render: (r) => r.name },
  { label: "Slug", render: (r) => <span className="font-mono text-xs text-grey">{r.slug}</span> },
  { label: "Order", render: (r) => r.order },
];
